import { useMemo } from "react";
import { parseCertificateExpiry } from "../utils";

export type CertificateExpirySeverity =
  | "neutral"
  | "warning"
  | "critical"
  | "expired";

export interface CertificateExpiryInfo {
  expiryDate: Date | null;
  daysUntilExpiry: number | null;
  severity: CertificateExpirySeverity;
}

/**
 * Computes certificate expiry information from a base64-encoded certificate.
 * Parses the X.509 notAfter date and classifies severity based on thresholds:
 * - >90 days: neutral
 * - ≤90 days: warning
 * - ≤30 days: critical
 * - past: expired
 */
export function useCertificateExpiry(
  certBase64: string | null | undefined,
): CertificateExpiryInfo {
  return useMemo(() => {
    if (!certBase64) {
      return { expiryDate: null, daysUntilExpiry: null, severity: "neutral" };
    }

    const expiryDate = parseCertificateExpiry(certBase64);
    if (!expiryDate) {
      return { expiryDate: null, daysUntilExpiry: null, severity: "neutral" };
    }

    const now = new Date();
    const diffMs = expiryDate.getTime() - now.getTime();
    const daysUntilExpiry = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    let severity: CertificateExpirySeverity;
    if (daysUntilExpiry <= 0) {
      severity = "expired";
    } else if (daysUntilExpiry <= 30) {
      severity = "critical";
    } else if (daysUntilExpiry <= 90) {
      severity = "warning";
    } else {
      severity = "neutral";
    }

    return { expiryDate, daysUntilExpiry, severity };
  }, [certBase64]);
}
